// Forgot password (WEB_PLAN.md §4.2, §5.1), the web twin of iOS ForgotPasswordView: an emailed code, then a new
// password; resetting signs in. GoTrue answers the same whether or not the address has an account, so this screen
// always says "If an account exists, we sent a code" and can't be used to find out who has one. If the new
// password can't be saved after the code was accepted, flows.resetPassword signs out again (no half-reset session)
// and the used code needs "Resend code".

import { KeyRound, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { providerNames, useAuthOptions } from '@/auth/authSettings';
import { authErrorMessage } from '@/auth/errors';
import { codeDigits, isCompleteCode, isEmailLike, normalizeEmail, resetPassword, sendPasswordReset } from '@/auth/flows';
import { isValidPassword } from '@/auth/passwordPolicy';
import { recalledEmail, rememberEmail } from '@/auth/pending';
import { AuthCardBody, AuthHeader, AuthPage } from './components/AuthCard';
import { CodeInput } from './components/CodeInput';
import { PasswordChecklist } from './components/PasswordChecklist';
import { ResendCodeButton } from './components/ResendCodeButton';
import { Caption, CodeSentRow, ErrorText, Field, Notice, PasswordInput, PrimaryButton, TextInput } from './components/ui';

const RESEND_AFTER_MS = 30_000;

/** No account enumeration: the same words whether or not the address has an account. */
const RESET_CODE_SENT_TEXT = 'If an account exists, we sent a code.';

export default function ForgotPasswordPage() {
  const options = useAuthOptions();
  const [email, setEmail] = useState(() => recalledEmail());
  /** The address the code went to (step 2), or null (step 1). */
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [availableAt, setAvailableAt] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const busy = sending || resetting;
  const canSend = isEmailLike(email) && !busy;
  const canReset = sentTo !== null && isCompleteCode(codeDigits(code)) && isValidPassword(password)
    && confirmation === password && !busy;

  const send = async (to: string): Promise<boolean> => {
    setSending(true);
    setError(null);
    try {
      await sendPasswordReset(to);
      setAvailableAt(Date.now() + RESEND_AFTER_MS);
      setCode('');
      return true;
    } catch (e) {
      setError(authErrorMessage(e));
      return false;
    } finally {
      setSending(false);
    }
  };

  const sendCode = async () => {
    if (!canSend) return;
    const to = normalizeEmail(email);
    rememberEmail(to);
    if (await send(to)) setSentTo(to);
  };

  const resend = async () => {
    if (!sentTo || busy || Date.now() < availableAt) return;
    await send(sentTo);
  };

  const reset = async () => {
    if (!canReset || !sentTo) return;
    setResetting(true);
    setError(null);
    try {
      await resetPassword(sentTo, code, password);
      setPassword('');
      setConfirmation('');
      // Signed in with the new password: AuthProvider takes over; stay busy until this page goes away.
    } catch (e) {
      setError(authErrorMessage(e));
      setResetting(false);
    }
  };

  const changeAddress = () => {
    setSentTo(null);
    setCode('');
    setError(null);
  };

  // Under "Send code": Apple / Google accounts have no password to reset (naming only the buttons that show).
  const providers = providerNames(options);
  const requestNote = providers
    ? `Signed up with ${providers}? You don't need a password: use that button on the sign-in screen.`
    : null;

  return (
    <AuthPage>
      <AuthHeader
        icon={sentTo === null ? <KeyRound className="h-6 w-6" aria-hidden /> : <LockKeyhole className="h-6 w-6" aria-hidden />}
        title={sentTo === null ? 'Forgot your password?' : 'Choose a new password'}
        subtitle={
          sentTo === null
            ? "We'll email a code to the address you signed up with."
            : 'Enter the code we emailed you (it can take a minute; look in spam too), then your new password.'
        }
      />
      {sentTo === null ? (
        <AuthCardBody as="form" onSubmit={() => void sendCode()}>
          <Field label="Email">
            {(id) => (
              <TextInput
                id={id}
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="name@company.com"
                aria-label="Email address"
                value={email}
                disabled={busy}
                autoFocus
                onChange={(e) => {
                  setEmail(e.target.value);
                  rememberEmail(e.target.value);
                }}
              />
            )}
          </Field>
          <ErrorText>{error}</ErrorText>
          <PrimaryButton
            type="submit"
            busy={sending}
            busyLabel="Sending…"
            icon={<Mail className="h-4 w-4" aria-hidden />}
            disabled={!canSend}
          >
            Send code
          </PrimaryButton>
          {requestNote && <Caption>{requestNote}</Caption>}
        </AuthCardBody>
      ) : (
        <AuthCardBody as="form" onSubmit={() => void reset()}>
          <CodeSentRow destination={sentTo} onChange={resetting ? undefined : changeAddress} />
          <Notice icon={<Mail className="h-3.5 w-3.5" aria-hidden />}>{RESET_CODE_SENT_TEXT}</Notice>
          <CodeInput value={code} onChange={setCode} disabled={resetting} autoFocus />
          <Field label="New password">
            {(id) => (
              <PasswordInput
                id={id}
                revealed={revealed}
                onToggleReveal={() => setRevealed((r) => !r)}
                autoComplete="new-password"
                placeholder="New password"
                value={password}
                disabled={resetting}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <Field label="Confirm password">
            {(id) => (
              <PasswordInput
                id={id}
                revealed={revealed}
                showsRevealButton={false}
                autoComplete="new-password"
                placeholder="Confirm password"
                value={confirmation}
                disabled={resetting}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            )}
          </Field>
          <PasswordChecklist password={password} confirmation={confirmation} />
          <ErrorText>{error}</ErrorText>
          <PrimaryButton
            type="submit"
            busy={resetting}
            busyLabel="Resetting…"
            icon={<ShieldCheck className="h-4 w-4" aria-hidden />}
            disabled={!canReset}
          >
            Reset password
          </PrimaryButton>
          <ResendCodeButton availableAt={availableAt} busy={sending} disabled={resetting} onClick={() => void resend()} />
        </AuthCardBody>
      )}
      <Link
        to="/sign-in"
        replace
        aria-disabled={resetting || undefined}
        onClick={(e) => {
          if (resetting) e.preventDefault();
        }}
        className="self-center text-sm font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        Back to sign in
      </Link>
    </AuthPage>
  );
}
